import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PLANS, INTRO_PLAN } from "../lib/constants";
import { toast } from "sonner";

export function Checkout() {
  const { planKey } = useParams();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [voucher, setVoucher] = useState(null);
  const plan = [...PLANS, INTRO_PLAN].find((p) => p.key === planKey);

  useEffect(() => {
    if (user === false) navigate(`/register?plan=${planKey}`);
    else if (user && !user.email_verified && user.role !== "admin") {
      navigate(`/verify-email?next=${encodeURIComponent(`/checkout/${planKey}`)}`);
    }
  }, [user, planKey, navigate]);

  useEffect(() => {
    if (user && user.voucher_code) {
      api.get("/vouchers/validate", { params: { code: user.voucher_code } })
        .then(({ data }) => { if (data.plans.includes(planKey)) setVoucher(data); })
        .catch(() => {});
    }
  }, [user, planKey]);

  if (!plan) return <div className="p-16 text-center text-slate-500">Unknown plan.</div>;
  if (!user) return <div className="p-16 text-center text-slate-400">Loading…</div>;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  const onTrial = plan.trial && !user.trial_used;
  const tomorrow = new Date(Date.now() + 86400000).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const basePrice = Number(plan.price.replace("£", ""));
  const discPrice = voucher ? `£${(basePrice * (100 - voucher.percent) / 100).toFixed(2).replace(/\.00$/, "")}` : plan.price;

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#FAFAF7]">
      <div className="relative w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8" data-testid="checkout-card">
        <h1 className="font-display text-2xl font-bold tracking-tight">{plan.name} Membership</h1>
        {plan.key !== INTRO_PLAN.key && (
          <img src="/assets/cert-badge.webp" alt="Earn your Certificate of Completion — XpressPrompts Starter Training"
            title="Includes the XpressPrompts™ Starter Training — earn your Certificate of Completion"
            draggable={false} data-testid="checkout-cert-badge" className="absolute top-4 right-4 w-14 h-14 drop-shadow-md" />
        )}
        {voucher && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3.5 py-2.5 text-sm text-amber-900 flex items-center justify-between" data-testid="checkout-voucher-note">
            <span>Code <strong>{voucher.code}</strong> applied — {voucher.percent}% off</span>
            <span className="font-bold"><s className="text-amber-400 font-normal">{plan.price}</s> {discPrice}</span>
          </div>
        )}
        {onTrial ? (
          <>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-4xl font-extrabold text-emerald-700">£0</span>
              <span className="text-slate-500">due today</span>
            </div>
            <div className="mt-3 bg-emerald-50 border border-emerald-100 rounded-xl p-3.5 text-sm text-emerald-900" data-testid="checkout-trial-note">
              <strong>1-day free trial.</strong> Your card is verified today but not charged. On <strong>{tomorrow}</strong> it will be automatically charged <strong>{discPrice}</strong>, then {plan.price}/year. Cancel any time in the first 24 hours from My Account and pay nothing. By continuing you request immediate access to digital content and acknowledge that once the trial ends and payment is taken, the annual payment is non-refundable and the 14-day cooling-off right no longer applies (see Subscription Terms).
            </div>
          </>
        ) : (
          <>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-4xl font-extrabold">{discPrice}</span>
              <span className="text-slate-500">{plan.per}</span>
            </div>
            <p className="text-sm text-slate-500 mt-1.5">{plan.trial ? `Renews annually at ${plan.price}. ${user.trial_used ? "Your free trial has already been used, so payment is taken today." : ""}` : plan.renews}</p>
          </>
        )}
        <ul className="mt-5 space-y-2.5">
          {plan.features.slice(0, 4).map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-700"><Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {f}</li>
          ))}
        </ul>
        <button data-testid="checkout-pay-btn" onClick={pay} disabled={busy}
          className="mt-7 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl active:scale-[0.98] transition-all disabled:opacity-60 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} {onTrial ? plan.startCta : plan.key === "pro_intro" ? "JOIN NOW — PAY £4.99" : `PAY ${plan.price} NOW`}
        </button>
        <p className="text-xs text-slate-400 text-center mt-3">Secure checkout powered by Stripe. Cards, Apple Pay &amp; Google Pay supported where available.</p>
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      if (attempts > 10) { setStatus("timeout"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || data.payment_status === "no_payment_required") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
      } catch {}
      setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#FAFAF7]">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center" data-testid="payment-success-card">
        {status === "checking" && (<><Loader2 size={40} className="animate-spin text-emerald-600 mx-auto" /><p className="mt-4 text-slate-600 font-medium">Confirming your payment…</p></>)}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><Check size={30} /></div>
            <h1 className="font-display text-2xl font-bold mt-4">You're in!</h1>
            <p className="text-slate-500 text-sm mt-2">Your membership is active. Your searchable dictionary is ready.</p>
            <button data-testid="go-to-dictionary-btn" onClick={() => navigate("/app")}
              className="mt-6 w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 rounded-xl transition-all">Open the Dictionary</button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle size={40} className="text-red-400 mx-auto" />
            <h1 className="font-display text-xl font-bold mt-4">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
            <p className="text-slate-500 text-sm mt-2">{status === "timeout" ? "Your payment is taking a moment. Check your email or refresh shortly." : "We couldn't confirm this payment."}</p>
            <Link to="/pricing" className="inline-block mt-5 text-emerald-700 font-semibold hover:underline">Back to pricing</Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16 bg-[#FAFAF7]">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center" data-testid="payment-cancel-card">
        <h1 className="font-display text-2xl font-bold">Checkout cancelled</h1>
        <p className="text-slate-500 text-sm mt-2">No payment was taken. You can pick a plan whenever you're ready.</p>
        <Link to="/pricing" data-testid="cancel-back-pricing" className="inline-block mt-6 bg-slate-900 text-white font-semibold px-6 py-3 rounded-xl">Back to pricing</Link>
      </div>
    </div>
  );
}
